import { useState, useCallback } from 'react';
import { savePLData } from '../../services/plDataService';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

function UserOnboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    brandName: '',
    industry: '',
    annualRevenue: '',
    integrations: {
      shopify: false,
      googleAnalytics: false,
      email: false
    },
    plFile: null
  });

  const industries = [
    'Fashion & Apparel',
    'Electronics',
    'Beauty & Personal Care',
    'Home & Garden',
    'Food & Beverage',
    'Health & Wellness',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          [name]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Only accept Excel or CSV files
    if (!file.name.match(/\.(xlsx|xls|csv)$/)) {
      toast.error('Please upload an Excel or CSV file');
      return;
    }

    setFormData(prev => ({ ...prev, plFile: file }));
  }, []);

  const processPlFile = async (file) => {
    // Here we would normally process the Excel/CSV file
    // For now, we'll use sample data
    return {
      userId: 'current-user-id', // Replace with actual user ID
      revenue: {
        recurring: 120000,
        new: 80000,
        total: 200000
      },
      costs: {
        fixed: 50000,
        variable: 100000,
        total: 150000
      },
      profitability: {
        grossMargin: 0.45,
        netProfit: 0.25
      },
      categories: {
        marketing: 30000,
        operations: 40000,
        discounts: 15000,
        returns: 5000
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      if (formData.plFile) {
        const plData = await processPlFile(formData.plFile);
        await savePLData(plData);
        toast.success('P&L data uploaded successfully');
      }

      // Navigate to data analysis page
      navigate('/automation/data');
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error('Failed to save data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Brand Profile Setup</h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Name
                </label>
                <input
                  type="text"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300"
                  required
                >
                  <option value="">Select Industry</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Annual Revenue
                </label>
                <select
                  name="annualRevenue"
                  value={formData.annualRevenue}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300"
                  required
                >
                  <option value="">Select Revenue Range</option>
                  <option value="0-100k">$0 - $100k</option>
                  <option value="100k-500k">$100k - $500k</option>
                  <option value="500k-1m">$500k - $1M</option>
                  <option value="1m-5m">$1M - $5M</option>
                  <option value="5m+">$5M+</option>
                </select>
              </div>
            </div>
          </div>

          {/* Integrations */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Integrations</h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="shopify"
                  checked={formData.integrations.shopify}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-[#0049ac]"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Shopify Store
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="googleAnalytics"
                  checked={formData.integrations.googleAnalytics}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-[#0049ac]"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Google Analytics
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="email"
                  checked={formData.integrations.email}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-[#0049ac]"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Email Platform
                </label>
              </div>
            </div>
          </div>

          {/* P&L Upload */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">P&L Statement</h2>
            <div className="mt-2 flex justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-10">
              <div className="text-center">
                <div className="mt-4 flex text-sm leading-6 text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-[#0049ac] focus-within:outline-none focus-within:ring-2 focus-within:ring-[#0049ac] focus-within:ring-offset-2 hover:text-[#0049ac]/90"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs leading-5 text-gray-600">Excel or CSV up to 10MB</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-[#0049ac] text-white rounded-lg hover:bg-[#0049ac]/90"
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserOnboarding;